import { Component, HostListener, Inject, OnDestroy, TemplateRef, ViewChild, ViewEncapsulation } from '@angular/core';
import { MatDialog } from '@angular/material/dialog';
import { EMPTY, Subscription } from 'rxjs';
import { catchError, finalize } from 'rxjs/operators';
import { SitesService } from 'src/app/mobile/shared/sites.service';
import { WhatsappTemplatesService } from 'src/app/parameters/message-templates/whatsapp-templates/whatsapp-templates.service';
import { ConfirmDialogComponent } from 'src/app/shared/components/confirm-dialog/confirm-dialog.component';
import { SearchBarService } from 'src/app/shared/components/search-bar/search-bar.service';
import { TableColumnTypeEnum, TableFilterTypeEnum } from 'src/app/shared/components/table/shared/enums';
import { TableConfig, TableFilters } from 'src/app/shared/components/table/shared/models';
import { TableLabelCellEnum } from 'src/app/shared/components/table/shared/models/table-label-cell.enum';
import { BasicService } from 'src/app/shared/services/basic.service';
import { WhatsappService } from 'src/app/shared/strategies/channels';
import { WhatsAppTemplateStatusEnum } from './shared';
import { WhatsAppTemplateUsageEnum } from './shared/enums/whastapp-template-usage.enum';
import { WhatsappTemplateScenarioDependency } from './shared/models/whatsapp-template-scenario-dependency.model';
import { WhatsappTemplateSettings } from './shared/models/whatsapp-template-settings.model';

@Component({
  selector: 'app-whatsapp-templates',
  templateUrl: './whatsapp-templates.component.html',
  styleUrls: ['./whatsapp-templates.component.scss'],
  encapsulation: ViewEncapsulation.None,
})
export class WhatsappTemplatesComponent implements OnDestroy {
  selectedTabIndex = 0;
  config: TableConfig = {
    displayedColumns: ['templateName', 'sites', 'status', 'languages', 'usages', 'date', 'actions'],
    columns: [
      {
        name: 'templateName',
        translationKey: 'parameters/whatsapp-templates/name',
        property: 'templateNameRef',
        type: TableColumnTypeEnum.TEMPLATE_REF,
      },
      {
        name: 'sites',
        translationKey: 'parameters/whatsapp-templates/account-id',
        property: 'site',
        type: TableColumnTypeEnum.SITE,
        sortEnabled: true,
      },
      {
        name: 'status',
        translationKey: 'parameters/whatsapp-templates/status/label',
        property: 'statusLabel',
        type: TableColumnTypeEnum.LABEL,
        sortEnabled: true,
      },
      {
        name: 'languages',
        translationKey: 'parameters/whatsapp-templates/language',
        property: 'languagesBodyRef',
        type: TableColumnTypeEnum.TEMPLATE_REF,
      },
      {
        name: 'usages',
        translationKey: 'parameters/whatsapp-templates/usage',
        property: 'usages',
        type: TableColumnTypeEnum.LIST,
        sortEnabled: true,
      },
      {
        name: 'date',
        translationKey: 'parameters/whatsapp-templates/last-modification',
        property: 'last_modified_at',
        type: TableColumnTypeEnum.DATE,
        sortEnabled: true,
      },
    ],
    actions: [
      {
        tooltip: 'delete',
        clickFn: element => this.askForDelete(element),
        icon: 'delete',
        color: 'warn',
      },
    ],
    disableLineClass: ['app-whatsapp-templates', 'disabled'],
  };
  filters: TableFilters = {
    date: {
      name: 'lastEditionDate',
      placeholder: '',
      type: TableFilterTypeEnum.DATE,
    },
    sites: {
      name: 'accountIds',
      placeholder: 'parameters/whatsapp-templates/account-id',
      type: TableFilterTypeEnum.MULTI_CHOICE,
    },
    status: {
      name: 'status',
      placeholder: 'parameters/whatsapp-templates/status/label',
      type: TableFilterTypeEnum.MULTI_CHOICE,
    },
    languages: {
      name: 'languages',
      placeholder: 'parameters/whatsapp-templates/language',
      type: TableFilterTypeEnum.MULTI_CHOICE,
    },
    usages: {
      name: 'usages',
      placeholder: 'parameters/whatsapp-templates/usages',
      type: TableFilterTypeEnum.MULTI_CHOICE,
    },
  };

  answerTemplatesDependencies: WhatsappTemplateScenarioDependency[] = [];
  @ViewChild('answerTemplatesDependenciesTemplateRef', { static: true })
  answerTemplatesDependenciesTemplateRef: TemplateRef<any>;

  private subscriptions = new Subscription();

  @HostListener('window:resize')
  onWindowResizeUpdateActionsPosition() {
    this.searchBarService.checkScrollPosition();
  }

  @ViewChild('errorTemplateRef') errorTemplateRef: TemplateRef<any>;
  @ViewChild('templateNameRef', { static: true }) templateNameRef: TemplateRef<any>;
  @ViewChild('languagesBodyRef', { static: true }) languagesBodyRef: TemplateRef<any>;

  constructor(
    public dialog: MatDialog,
    private searchBarService: SearchBarService,
    private whatsappTemplatesService: WhatsappTemplatesService,
    private basicService: BasicService,
    private sitesService: SitesService,
    whatsappService: WhatsappService,
    @Inject('AlertingFactory') private alertingService: any
  ) {
    this.searchBarService.sort = { active: 'date', direction: 'desc' };
    this.searchBarService.referer$ = this.whatsappTemplatesService.retrieveTemplates;
    this.searchBarService.formatter = (element: WhatsappTemplateSettings) => {
      const { site_id, languages, category, status, element_name, text } = element;
      if (!this.sitesService.sitesV2Map.has(site_id)) {
        element.disable = true;
      } else {
        element.site = this.sitesService.enrichSiteWithLogo(this.sitesService.sitesV2Map.get(site_id));
        element.disable = [WhatsAppTemplateStatusEnum.deleted, WhatsAppTemplateStatusEnum.pending_deletion].includes(
          status
        );
      }
      let color = null;
      switch (status) {
        case WhatsAppTemplateStatusEnum.approved:
          color = TableLabelCellEnum.GREEN;
          break;
        case WhatsAppTemplateStatusEnum.paused:
        case WhatsAppTemplateStatusEnum.pending:
        case WhatsAppTemplateStatusEnum.pending_deletion:
        case WhatsAppTemplateStatusEnum.deleted:
          color = TableLabelCellEnum.GREY;
          break;
        case WhatsAppTemplateStatusEnum.rejected:
          color = TableLabelCellEnum.RED;
          break;
        case WhatsAppTemplateStatusEnum.bsp_temporary:
        case WhatsAppTemplateStatusEnum.in_appeal:
        case WhatsAppTemplateStatusEnum.limit_exceeded:
        case WhatsAppTemplateStatusEnum.not_migrated_to_cloudapi:
          color = TableLabelCellEnum.YELLOW;
          break;
      }
      element.statusLabel = {
        text: this.basicService.translate(`parameters/whatsapp-templates/status/${status || 'unknown'}`),
        color,
      };

      element.templateNameRef = {
        templateRef: this.templateNameRef,
        context: {
          url: whatsappTemplatesService.getTemplateDetailsPath(`${element_name}`, site_id),
          name: element_name,
          categoryLabel: category && basicService.translate(`parameters/whatsapp-templates/category/${category}/label`),
        },
      };

      const languagesLabel = (languages || [])
        .map((language: string) => this.basicService.translate(language))
        .join(', ');

      element.languagesBodyRef = {
        templateRef: this.languagesBodyRef,
        context: {
          languages: languagesLabel,
          text,
        },
      };
    };

    this.filters.sites.items = this.sitesService.sitesV2
      .filter(({ type }) => type === whatsappService.getType())
      .map(({ id, siteName }) => ({ id, label: siteName }));

    this.filters.status.items = Object.keys(WhatsAppTemplateStatusEnum).map(id => ({
      id,
      label: this.basicService.translate(`parameters/whatsapp-templates/status/${id}`),
    }));

    this.filters.languages.items = this.whatsappTemplatesService.getSupportedLanguages();

    this.filters.usages.items = [
      {
        id: WhatsAppTemplateUsageEnum.advisor,
        label: this.basicService.translate('parameters/whatsapp-templates/details/main/usages/advisor'),
      },
      {
        id: WhatsAppTemplateUsageEnum.bot,
        label: this.basicService.translate('parameters/whatsapp-templates/details/main/usages/bot'),
      },
    ];

    this.searchBarService.submitQuery();
  }

  primaryButtonAction = () => {
    this.whatsappTemplatesService.navigateToCreate();
  };

  secondaryButtonAction = () => {
    this.basicService.showSpinner();
    this.whatsappTemplatesService
      .synchronizeStatuses()
      .pipe(
        catchError(() => {
          this.basicService.hideSpinner();
          this.alertingService.error('bug/unexpected-error');
          return EMPTY;
        })
      )
      .subscribe({
        next: () => this.searchBarService.searchAndSortBS.next(true),
      });
  };

  private async askForDelete(template: WhatsappTemplateSettings) {
    const { site_id, element_name } = template;
    this.answerTemplatesDependencies = await this.whatsappTemplatesService
      .findTemplateUsageDependencies(site_id, element_name)
      .toPromise();
    const confirmBtnText = this.answerTemplatesDependencies.length ? null : this.basicService.translate('remove');
    const dialogRef = this.dialog.open(ConfirmDialogComponent, {
      data: {
        title: this.basicService.translate('parameters/whatsapp-templates/remove/title'),
        description: this.basicService.translate('parameters/whatsapp-templates/remove/confirm', {
          templateName: element_name,
        }),
        templateRef: this.answerTemplatesDependenciesTemplateRef,
        cancelBtnText: this.basicService.translate('cancel'),
        confirmBtnText,
      },
    });
    dialogRef.afterClosed().subscribe(result => {
      if (result) {
        const { site_id, element_name } = template;
        this.basicService.showSpinner();
        this.whatsappTemplatesService
          .remove(site_id, element_name)
          .pipe(finalize(() => this.basicService.hideSpinner()))
          .subscribe({
            next: () => {
              this.searchBarService.searchAndSortBS.next(true);
              this.basicService.toastSuccess('ok');
            },
          });
      }
    });
  }
  ngOnDestroy(): void {
    this.subscriptions?.unsubscribe();
    this.searchBarService.onDidLeave();
  }
}
